import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { tableColumnPropType } from '../utils/propTypes';
import classNames from 'classnames';
import InlineAlert from './InlineAlert';
const propTypes = {
  /**
   * Array of category objects for grouping rows
   */
  categories: PropTypes.arrayOf(PropTypes.object),
  /**
   * Array of column definitions
   */
  columns: PropTypes.arrayOf(tableColumnPropType).isRequired,
  /**
   * Array of data rows to display
   */
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  /**
   * The number of columns docked to the left of the table
   */
  fixedColumnCount: PropTypes.number.isRequired,
  /**
   * The height of the fixed table header, if there is one.
   * This is used to calculate the top position of the category rows.
   * If there is no fixed header, this should be set to 0.
   */
  headerHeight: PropTypes.number,
  /**
   * Array of column headers to display in the sub-category rows
   */
  subCategoryHeaders: PropTypes.array,
  /**
   * Object of theme-like props that will not change after the component mounts
   */
  theme: PropTypes.shape({
    /**
     * Table background color
     */
    backgroundColor: PropTypes.oneOf(['white', 'gray-05', 'gray-10']),
    /**
     * Determines the vertical alignment of the cells across the entire table.
     * default: middle alignment,
     * option: top alignment
     */
    cellsAlignment: PropTypes.oneOf(['middle', 'top']),
    /**
     * Determines the vertical padding of the cells across the entire table.
     * normal: 9px top and bottom padding,
     * compact: 5px top and bottom padding,
     */
    rowSpacing: PropTypes.oneOf(['normal', 'compact']),
    /**
     * Flag to determine if zebra striping will be used
     */

    useZebraStriping: PropTypes.bool,
    /**
     * Determines the order of the zebra stripes
     * odd: the first row is gray (default),
     * even: the first row is white
     */
    zebraStriping: PropTypes.oneOf(['odd', 'even'])
  })
};
const defaultProps = {
  headerHeight: 0,
  theme: {
    backgroundColor: 'white',
    cellsAlignment: 'middle',
    rowSpacing: 'normal',
    useZebraStriping: false,
    zebraStriping: 'odd'
  }
};
class TableBody extends Component {
  constructor(props) {
    super(props);
    this.handleRowFocus = rowId => {
      this.setState({
        focusWithinRow: rowId
      });
    };
    this.handleRowBlur = () => {
      this.setState({
        focusWithinRow: undefined
      });
    };
    this.state = {
      focusWithinRow: undefined
    };
    this.isSafari = window.safari !== undefined;
  }
  render() {
    const {
      columns,
      data,
      fixedColumnCount,
      categories,
      headerHeight,
      subCategoryHeaders,
      theme: {
        backgroundColor,
        cellsAlignment,
        rowSpacing,
        useZebraStriping,
        zebraStriping
      }
    } = this.props;
    const {
      focusWithinRow
    } = this.state;
    const hasFixedColumns = fixedColumnCount > 0;
    let subCategoryCount = 0;
    const categoryRowHeight = 48;
    const rowHeaderColumn = columns.find(col => col.rowHeader);
    const getHeaderRowMarkup = (row, rowIndex) => {
      const cells = [];
      for (let i = 0; i < columns.length; i++) {
        if (!columns[i].rowHeader) {
          cells.push(/*#__PURE__*/React.createElement("td", {
            key: 'cell-header-' + row.id + columns[i].dataKey,
            className: "asc-table-hidden"
          }));
        } else if (columns[i].rowHeader) {
          cells.push(/*#__PURE__*/React.createElement("td", {
            className: "asc-table-component-row-header-cell",
            colSpan: columns.length - i,
            headers: `col-header-${columns[i].dataKey}`,
            key: 'cell-header-' + row.id + columns[i].dataKey
          }, /*#__PURE__*/React.createElement("div", {
            className: "asc-table-component-row-header-cell--wrapper"
          }, columns[i].cell ? columns[i].cell(row[columns[i].dataKey], row.id, row) : row[columns[i].dataKey])));
          break;
        }
      }
      return /*#__PURE__*/React.createElement("tr", {
        key: 'row-header-' + row.id,
        className: cx('asc-table-component-row-header', {
          'asc-table-component-row--odd': useZebraStriping && rowIndex % 2 === 0,
          'asc-table-component-row--even': useZebraStriping && rowIndex % 2 !== 0
        })
      }, cells);
    };
    const getRowMarkup = (rows, categoryContent) => {
      const cellBaseClassNames = cx('asc-table-component-cell', {
        'asc-table-component-cell--top-aligned': cellsAlignment === 'top',
        'asc-table-component-cell--compact': rowSpacing === 'compact'
      });
      return rows.map((row, index) => {
        const showInlineAlert = row.inlineAlert && row.inlineAlert.message;
        if (showInlineAlert) {
          if (!row.inlineAlert.type) {
            row.inlineAlert.type = 'info';
          }
        }
        let rowBaseClassNames = cx('asc-table-component-row', {
          'asc-table-component-row--compact': rowSpacing === 'compact',
          'asc-table-component-row-focus-within': focusWithinRow === row.id,
          'asc-table-component-row--odd': useZebraStriping && index % 2 === 0,
          'asc-table-component-row--even': useZebraStriping && index % 2 !== 0
        });
        let rowClassNames = cx(rowBaseClassNames, {
          'asc-table-component-row-non-last': !(rows.length - 1 === index) && !showInlineAlert,
          'asc-table-component-row-last': rows.length - 1 === index && !showInlineAlert
        });
        let alertMessageColIndex = -1;
        return /*#__PURE__*/React.createElement(React.Fragment, {
          key: 'row-' + row.id
        }, rowHeaderColumn && getHeaderRowMarkup(row, index), /*#__PURE__*/React.createElement("tr", {
          className: rowClassNames,
          id: 'row-' + row.id,
          key: 'row-' + row.id,
          onFocus: () => this.handleRowFocus(row.id),
          onBlur: () => this.handleRowBlur()
        }, columns.map((column, index) => {
          let cell;
          if (column.dataKey === 'category') {
            cell = categoryContent;
          } else if (column.cell && !column.rowHeader) {
            cell = column.cell(row[column.dataKey], row.id, row);
          } else {
            cell = row[column.dataKey];
          }
          if (column.hidden || column.rowHeader) {
            cell = /*#__PURE__*/React.createElement("span", {
              className: "asc-acc-hidden"
            }, cell);
          }
          const isInlineAlertColumn = column.dataKey === 'inlineAlert';
          if (isInlineAlertColumn) {
            alertMessageColIndex = column.alertMessageColIndex;
          }
          let cellClassNames = cx(cellBaseClassNames, `asc-table--bg-${backgroundColor}`, {
            'asc-table-component-cell--fixed': column.fixed,
            'asc-table-component-cell--editable': column.cell,
            'asc-table-component-cell--fixed-last': index === fixedColumnCount - 1,
            'asc-table-hidden': column.hidden || column.rowHeader,
            'asc-table-component-cell--row-header': column.rowHeader,
            'asc-table-component-inline-alert': isInlineAlertColumn,
            [`asc-table-component-inline-alert--${row.inlineAlert?.type}`]: showInlineAlert && isInlineAlertColumn
          });
          let cellStyles = {
            textAlign: column.align ? column.align : 'left',
            minWidth: column.width ? column.width : 'auto'
          };
          if (column.fixed && column.left) {
            cellStyles.left = column.left;
          }
          return /*#__PURE__*/React.createElement("td", {
            className: cellClassNames,
            key: 'cell-' + row.id + column.dataKey,
            style: cellStyles,
            headers: `col-header-${column.dataKey}`
          }, cell);
        })), showInlineAlert && alertMessageColIndex >= 0 && (() => {
          const alertRowClassNames = cx(rowBaseClassNames, {
            'asc-table-component-row-non-last': !(rows.length - 1 === index),
            'asc-table-component-row-last': rows.length - 1 === index
          });
          return /*#__PURE__*/React.createElement("tr", {
            className: alertRowClassNames,
            onFocus: () => this.handleRowFocus(row.id),
            onBlur: () => this.handleRowBlur()
          }, /*#__PURE__*/React.createElement("td", {
            className: classNames(cellBaseClassNames, 'asc-table-hidden', 'asc-table-component-inline-alert', `asc-table-component-inline-alert--${row.inlineAlert?.type}`, {
              'asc-table-component-cell--fixed': hasFixedColumns,
              'asc-table-component-cell--fixed-last': hasFixedColumns
            }),
            colSpan: hasFixedColumns ? fixedColumnCount : alertMessageColIndex
          }), hasFixedColumns && alertMessageColIndex > 0 && /*#__PURE__*/React.createElement("td", {
            className: cellBaseClassNames,
            colSpan: alertMessageColIndex
          }), /*#__PURE__*/React.createElement("td", {
            className: cellBaseClassNames,
            colSpan: hasFixedColumns ? Object.keys(columns).length - alertMessageColIndex - fixedColumnCount : Object.keys(columns).length - alertMessageColIndex
          }, /*#__PURE__*/React.createElement("div", {
            id: 'row-' + row.id + '-inline-alert-message',
            "aria-hidden": true,
            role: "presentation"
          }, /*#__PURE__*/React.createElement(InlineAlert, {
            message: row.inlineAlert.message,
            type: row.inlineAlert.type,
            rowId: row.id
          }))));
        })());
      });
    };
    const getCategoryRowMarkup = (category, index, topValue, rowClass, displayHeaders) => {
      let categoryCellClassNames = cx(`asc-table--bg-${backgroundColor}`, {
        'asc-table-component-cell--fixed': hasFixedColumns,
        'asc-table-component-cell--fixed-last': hasFixedColumns
      });
      let categoryRowClassNames = cx(rowClass, `asc-table--bg-${backgroundColor}`, {
        ['asc-table-component-row--' + category.color]: category.color
      });
      let colSpan;
      if (displayHeaders && !hasFixedColumns) {
        colSpan = columns.findIndex(col => col.dataKey === displayHeaders[0]) - 1;
      } else if (hasFixedColumns) {
        colSpan = fixedColumnCount - 1;
      } else {
        colSpan = columns.length - 1;
      }
      return /*#__PURE__*/React.createElement("tr", {
        className: categoryRowClassNames,
        "aria-hidden": "true",
        role: "presentation"
      }, /*#__PURE__*/React.createElement("td", {
        "aria-hidden": "true",
        className: "asc-table-hidden"
      }), /*#__PURE__*/React.createElement("td", {
        colSpan: colSpan,
        id: category.key,
        className: categoryCellClassNames,
        style: {
          top: topValue + 'px',
          zIndex: index + 13
        },
        "aria-hidden": "true"
      }, category.cell ? category.cell(category.label) : /*#__PURE__*/React.createElement("span", {
        className: "asc-table-component-category-row-info"
      }, /*#__PURE__*/React.createElement("span", null, category.label.toUpperCase()), /*#__PURE__*/React.createElement("span", {
        className: "asc-font-light"
      }, category.value?.toUpperCase()))), hasFixedColumns && !displayHeaders && /*#__PURE__*/React.createElement("td", {
        colSpan: columns.length - fixedColumnCount,
        className: `asc-table--bg-${backgroundColor}`,
        style: {
          top: topValue + 'px',
          zIndex: index + 12
        },
        "aria-hidden": "true"
        // eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role
        ,
        role: "presentation"
      }), displayHeaders && columns.slice(colSpan + 1).map((col, i) => {
        return /*#__PURE__*/React.createElement("td", {
          className: `asc-table--bg-${backgroundColor} asc-table-component-category-row-header`,
          style: {
            top: topValue + 'px',
            zIndex: index + 12,
            textAlign: col.align ? col.align : 'left'
          },
          "aria-hidden": "true"
          // eslint-disable-next-line jsx-a11y/no-interactive-element-to-noninteractive-role
          ,
          role: "presentation",
          key: `sub-cat-header-${i}`
        }, displayHeaders.includes(col.dataKey) && /*#__PURE__*/React.createElement("span", null, col.header.toUpperCase()));
      }));
    };
    return /*#__PURE__*/React.createElement("tbody", {
      ref: node => {
        this.tbodyNode = node;
      },
      className: cx({
        [`asc-table-component-zebra-stripes--${zebraStriping}`]: useZebraStriping
      })
    }, categories ? categories.map((category, categoryIndex) => {
      let categoryRows = data.filter(row => row.category === category.key);
      return categoryRows.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, {
        key: category.key
      }, getCategoryRowMarkup(category, categoryIndex,
      // Fix for sticky category row display issue in Safari
      this.isSafari ? 0 : headerHeight, `asc-table-component-category-row asc-sub-heading-small asc-table--bg-${backgroundColor}`), category.subCategories ? category.subCategories.map(subCategory => {
        let subCategoryRows = categoryRows.filter(row => row.subCategory === subCategory.key);
        return subCategoryRows.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, {
          key: subCategory.key
        }, getCategoryRowMarkup(subCategory, subCategoryCount++,
        // Fix for sticky subcategory row display issue in Safari
        this.isSafari ? categoryRowHeight : headerHeight + categoryRowHeight, 'asc-table-component-sub-category-row asc-sub-heading-small', subCategoryHeaders), getRowMarkup(subCategoryRows, category.label + ' ' + subCategory.label));
      }) : getRowMarkup(categoryRows, category.label));
    }) : getRowMarkup(data));
  }
}
TableBody.propTypes = propTypes;
TableBody.defaultProps = defaultProps;
export default TableBody;